/**
 * Client-side usage analytics for the shell.
 *
 * Three rules shape everything here:
 *
 * 1. **Fail silently.** Analytics must never break the desktop and must never
 *    spam the console. Every path is wrapped; a dead endpoint is invisible.
 * 2. **Batch, never per-event.** Events accumulate in `analytics-buffer.mjs`
 *    and leave on a timer, on size, or on unload.
 * 3. **The client never claims identity.** No user id is sent. The server reads
 *    it from the JWT. All this file contributes is a visit id and *what* the
 *    user did.
 *
 * Deliberately does NOT import `osStore` — `store.ts` calls into here from
 * `openApp`, and a cycle between the two would bite at module-init time. The
 * token is read from the same localStorage key `osStore` writes.
 */
import {
  FLUSH_INTERVAL_MS,
  createEventBuffer,
  resolveSessionId,
  type PendingEvent,
} from "./analytics-buffer.mjs";

const ENDPOINT = "/api/analytics";

const buffer = createEventBuffer();
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;
let ended = false;

function mintId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** Stable for the life of this browser tab, and across reloads within it. */
const sessionId = resolveSessionId(
  typeof sessionStorage !== "undefined" ? sessionStorage : null,
  mintId
);

function token(): string | null {
  try {
    return localStorage.getItem("arclog.token");
  } catch {
    return null;
  }
}

/**
 * Send a batch. `beacon` mode is for unload, where fetch() is cancelled the
 * instant the page goes away — `sendBeacon` hands the request to the browser to
 * deliver after the document is gone, which is the only way `session.end` ever
 * arrives.
 */
function send(events: PendingEvent[], beacon: boolean): void {
  if (!events.length) return;
  const jwt = token();
  if (!jwt) return; // not signed in — nothing to attribute this to
  const payload = JSON.stringify({ sessionId, events, ...(beacon ? { token: jwt } : {}) });

  if (beacon) {
    try {
      // sendBeacon cannot set headers, hence the token in the body (the
      // analytics router moves it back to Authorization before verifying).
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    } catch {
      /* fall through to fetch — better a lost beacon than a thrown error */
    }
  }

  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Endpoint down: put the batch back so the next flush retries it.
      buffer.restore(events);
    });
  } catch {
    buffer.restore(events);
  }
}

/** Drain and send whatever is pending. */
export function flushAnalytics(beacon = false): void {
  try {
    send(buffer.take(), beacon);
  } catch {
    /* silent by design */
  }
}

/**
 * Queue one event. The only public write path — `ctx.analytics.track`, the
 * shell's own auto-capture and the session hooks all land here.
 */
export function trackEvent(
  event: string,
  opts: { appId?: string; props?: Record<string, unknown> } = {}
): void {
  try {
    if (!event || typeof event !== "string") return;
    buffer.push({
      event,
      ts: Date.now(),
      sessionId,
      ...(opts.appId ? { appId: opts.appId } : {}),
      ...(opts.props ? { props: opts.props } : {}),
    });
    if (buffer.shouldFlush()) flushAnalytics();
  } catch {
    /* silent by design */
  }
}

/** `session.end`, exactly once, however the page happens to go away. */
function endSession(): void {
  if (ended) return;
  ended = true;
  trackEvent("session.end");
  flushAnalytics(true);
}

/**
 * Start auto-capture. Idempotent — React StrictMode mounts effects twice in
 * development and a second `session.start` would double every visit count.
 */
export function startAnalytics(): void {
  if (started) return;
  started = true;

  trackEvent("session.start", {
    props: {
      // Screen shape only — enough to know whether the OS is being used on a
      // phone. No fingerprinting surface beyond what the UA already reveals.
      w: typeof window !== "undefined" ? window.innerWidth : 0,
      h: typeof window !== "undefined" ? window.innerHeight : 0,
      tz: (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        } catch {
          return "";
        }
      })(),
    },
  });
  flushAnalytics();

  timer = setInterval(() => flushAnalytics(), FLUSH_INTERVAL_MS);

  // `pagehide` is the reliable one — mobile Safari often never fires
  // `beforeunload`, and a backgrounded tab can be discarded outright, so
  // `visibilitychange` is the last chance to get the batch out.
  window.addEventListener("pagehide", endSession);
  window.addEventListener("beforeunload", endSession);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushAnalytics(true);
  });
}

/** Torn down only by tests / hot reload; the real shell runs for the session. */
export function stopAnalytics(): void {
  if (timer) clearInterval(timer);
  timer = null;
  started = false;
}

/** The visit id, exposed for debugging and for the future analytics app. */
export function analyticsSessionId(): string {
  return sessionId;
}
